import api from "./apiService";

/**
 * Fetch all collections accessible to the current user
 * @param {Object} options - Options for filtering collections
 * @param {number} options.communityId - Filter by specific community
 * @param {boolean} options.userOnly - Only fetch user collections
 * @returns {Promise<Array>} List of collections
 */
export const fetchCollections = async (options = {}) => {
    try {
        // Build query parameters
        const params = new URLSearchParams();
        
        if (options.communityId) {
            params.append('community_id', options.communityId);
        }
        
        if (options.userOnly !== undefined) {
            params.append('user_only', options.userOnly);
        }
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/song-collections${queryString}`);
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch collections for a specific community
 * @param {number} communityId - The community ID
 * @returns {Promise<Array>} List of community collections
 */
export const fetchCommunityCollections = async (communityId) => {
    try {
        const response = await api.get(`/song-collections/community/${communityId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch a collection by ID with optional song inclusion
 * @param {number} collectionId - The collection ID
 * @returns {Promise<Object>} Collection data with songs
 */
export const fetchCollectionById = async (collectionId) => {
    try {
        const response = await api.get(`/song-collections/${collectionId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new collection
 * @param {Object} collectionData - The collection data
 * @param {string} collectionData.name - The name of the collection
 * @param {string} collectionData.description - The description
 * @param {string} collectionData.owner_type - The owner type ('user' or 'community')
 * @param {number} collectionData.owner_id - The owner ID
 * @param {number} collectionData.community_id - Alternative to owner_type/owner_id for community collections
 * @param {Array} collectionData.song_ids - Optional array of song IDs to include
 * @returns {Promise<Object>} Created collection data
 */
export const createCollection = async (collectionData) => {
    try {
        const response = await api.post("/song-collections", collectionData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a collection directly for a community (admin only)
 * @param {number} communityId - The community ID
 * @param {Object} collectionData - The collection data
 * @returns {Promise<Object>} Created collection data
 */
export const createCommunityCollection = async (communityId, collectionData) => {
    try {
        const response = await api.post(`/song-collections/community/${communityId}`, collectionData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing collection
 * @param {number} collectionId - The ID of the collection to update
 * @param {Object} collectionData - The updated collection data
 * @returns {Promise<Object>} Updated collection data
 */
export const updateCollection = async (collectionId, collectionData) => {
    try {
        const response = await api.put(`/song-collections/${collectionId}`, collectionData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a collection
 * @param {number} collectionId - The ID of the collection to delete
 * @returns {Promise<Object>} Response message
 */
export const deleteCollection = async (collectionId) => {
    try {
        await api.delete(`/song-collections/${collectionId}`);
        return { message: "Collection deleted successfully" };
    } catch (error) {
        throw error;
    }
};

/**
 * Add a song to a collection
 * @param {number} collectionId - The collection ID
 * @param {number} songId - The song ID to add
 * @returns {Promise<Object>} Response message
 */
export const addSongToCollection = async (collectionId, songId) => {
    try {
        const response = await api.post(`/song-collections/${collectionId}/songs/${songId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove a song from a collection
 * @param {number} collectionId - The collection ID
 * @param {number} songId - The song ID to remove
 * @returns {Promise<Object>} Response message
 */
export const removeSongFromCollection = async (collectionId, songId) => {
    try {
        const response = await api.delete(`/song-collections/${collectionId}/songs/${songId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};